/**
 * Edit a couple's names.
 *
 * NAMES ONLY, AND EMAIL IS DELIBERATELY ABSENT. wedding_couples.email is the
 * login identity, it is CITEXT UNIQUE, and email_verified gates sign-in — an
 * operator "fixing a typo" here would set the couple's own book out of reach.
 * Changing it needs its own flow that revokes sessions and re-sends
 * verification, so it is not a field on this form.
 *
 * The names are stored TWICE and the server moves both: wedding_couples has
 * the account's pair, and wedding_books carries its own, which is the pair
 * printed on the cover and in every email. The success message says how many
 * books were rewritten so the operator can see it happened.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Alert, Typography, CircularProgress,
} from '@mui/material';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { CoupleRow } from '../weddingApi';

interface Props {
  open: boolean;
  couple: CoupleRow | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

const CoupleEditDialog: React.FC<Props> = ({ open, couple, onClose, onSaved, onError }) => {
  const [partner1, setPartner1] = React.useState('');
  const [partner2, setPartner2] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open || !couple) return;
    setPartner1(couple.partner1_name || '');
    setPartner2(couple.partner2_name || '');
    setSaving(false);
  }, [open, couple?.id]);

  if (!couple) return null;

  const valid =
    partner1.trim().length > 0 && partner2.trim().length > 0 &&
    partner1.length <= 120 && partner2.length <= 120;

  const save = async () => {
    setSaving(true);
    try {
      const r = await weddingApi.updateCouple(couple.id, {
        partner1_name: partner1.trim(),
        partner2_name: partner2.trim(),
      });
      onSaved(
        r.books_updated > 0
          ? `Names updated, including on ${r.books_updated} book${r.books_updated === 1 ? '' : 's'}.`
          : 'Names updated. This couple has no book yet.'
      );
    } catch (e) {
      onError((e as WeddingApiError).message || 'Could not update the names');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit names</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Partner 1" value={partner1} size="small" fullWidth disabled={saving}
            onChange={(e) => setPartner1(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label="Partner 2" value={partner2} size="small" fullWidth disabled={saving}
            onChange={(e) => setPartner2(e.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              This also rewrites the names on the couple&rsquo;s book, which is
              what the cover prints.
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Email address: {couple.email} — not editable here. It is the
            couple&rsquo;s sign-in identity, and changing it would lock them out
            until they re-verify.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoupleEditDialog;
